/**
 * Peppol Invoice PDF Generation Service
 * Simple black and white template for invoices and credit notes
 */
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Generate HTML for Peppol Invoice/Credit Note PDF
 * @param {Object} invoiceData - Peppol invoice data
 * @param {string} documentType - 'Invoice' or 'CreditNote'
 * @param {string} language - Language code (en, fr, nl)
 * @returns {string} HTML string for PDF generation
 */
export const generatePeppolInvoicePdfHtml = (invoiceData, documentType = 'Invoice', language = 'en') => {
  const {
    invoiceNo,
    issueDate,
    dueDate,
    currencyCode = 'EUR',
    notes,
    supplier = {},
    customer = {},
    payment = {},
    amounts = {},
    lineItems = [],
  } = invoiceData;

  // Check if payment details should be shown
  const hasPaymentDetails = payment.accountNumber || payment.termsNote;

  // Get labels based on language
  const labels = getLabels(language);
  const isCreditNote = documentType?.toLowerCase() === 'creditnote';
  const docTitle = isCreditNote ? labels.creditNote : labels.invoice;

  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Format customer address - break into 2 lines
  const customerAddressLines = customer.address ? customer.address.split(',') : [];
  const customerAddressLine1 = customerAddressLines[0]?.trim() || '';
  const customerAddressLine2 = customerAddressLines.slice(1).join(',').trim() || '';

  // Format supplier address - break into 2 lines
  const supplierAddressLines = supplier.address ? supplier.address.split(',') : [];
  const supplierAddressLine1 = supplierAddressLines[0]?.trim() || '';
  const supplierAddressLine2 = supplierAddressLines.slice(1).join(',').trim() || '';

  // Generate line items HTML
  const lineItemsHtml = lineItems.map((item, index) => {
    const rowClass = index % 2 === 1 ? 'alternate-row' : '';
    return `
      <tr class="${rowClass}">
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; font-size: 8pt; text-align: center;">${item.lineNumber || index + 1}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; font-size: 8pt;">
          ${item.name || 'Product'}
        </td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; text-align: center; font-size: 8pt;">${Number(item.quantity || 0).toFixed(2)}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; text-align: right; font-size: 8pt;">€${Number(item.taxableAmount || 0).toFixed(2)}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; text-align: center; font-size: 8pt;">${Number(item.taxPercentage || 0).toFixed(0)}%</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #ddd; text-align: right; font-size: 8pt;">€${Number(item.lineTotal || 0).toFixed(2)}</td>
      </tr>
    `;
  }).join('');

  const html = `
<!DOCTYPE html>
<html lang="${language}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${docTitle} - ${invoiceNo}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: 'Arial', 'Helvetica', sans-serif; 
      font-size: 8pt; 
      color: #000; 
      line-height: 1.3;
      background: white;
    }
    .page { 
      width: 794px; 
      min-height: 1123px; 
      padding: 30px; 
      margin: 0 auto; 
      background: white; 
      position: relative; 
    }
    .watermark {
      position: absolute;
      top: 30px;
      left: 30px;
      width: 50%;
      font-size: 32pt;
      font-weight: bold;
      color: rgba(0, 0, 0, 0.08);
      z-index: 0;
      text-transform: uppercase;
      
    }
    .header { 
      position: relative;
      z-index: 1;
      margin-bottom: 20px;
      min-height: 180px;
    }
    .supplier-info { 
      margin-top: 50px;
      margin-bottom: 15px;
      max-width: 45%;
    }
    .supplier-name { 
      font-size: 11pt; 
      font-weight: bold; 
      color: #000; 
      margin-bottom: 4px; 
    }
    .supplier-details { 
      font-size: 8pt; 
      color: #000; 
      line-height: 1.4; 
    }
    .invoice-info { 
      position: absolute;
      top: 0px;
      right: 30px;
      text-align: left;
      max-width: 45%;
    }
    .invoice-date {
      font-size: 8pt;
      color: #000;
      margin-bottom: 3px;
    }
    .invoice-number-line {
      font-size: 8pt;
      color: #000;
      margin-bottom: 6px;
    }
    .invoice-number-line .label {
      font-size: 8pt;
      display: inline;
    }
    .invoice-number-line .value { 
      font-size: 10pt; 
      font-weight: bold; 
      display: inline;
      margin-left: 4px;
    }
    .customer-section { 
      font-size: 8pt; 
      color: #000; 
      line-height: 1.5;
      margin-top: 15px;
      text-align: left;
    }
    .customer-info {
      margin-bottom: 20px;
      padding: 10px;
      border: 1px solid #333;
    }
    .customer-label { 
      font-size: 9pt; 
      font-weight: 600; 
      color: #000; 
      margin-bottom: 4px; 
    }
    .customer-details { 
      font-size: 8pt; 
      color: #000; 
      line-height: 1.4; 
    }
    table { 
      width: 100%; 
      border-collapse: collapse; 
      margin-bottom: 20px;
      page-break-inside: auto;
    }
    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    thead {
      display: table-header-group;
    }
    th { 
      background: #fff; 
      color: #000; 
      padding: 8px 8px; 
      text-align: left; 
      font-weight: 600; 
      font-size: 8pt;
      border-bottom: 2px solid #000;
      border-top: 2px solid #000;
    }
    .alternate-row {
      background-color: #f5f5f5;
    }
    .payment-details {
      margin-top: 15px;
      width: 45%;
      border: 1px solid #333;
      padding: 10px;
      float: left;
    }
    .payment-details-label {
      font-weight: 600;
      color: #000;
      margin-bottom: 6px;
      font-size: 8pt;
      text-decoration: underline;
    }
    .payment-details-content {
      font-size: 8pt;
      line-height: 1.4;
    }
    .totals { 
      margin-top: 15px; 
      margin-left: auto; 
      width: 45%; 
      border-top: 2px solid #000;
      padding-top: 8px;
      float: right;
    }
    .totals-row { 
      display: flex; 
      justify-content: space-between; 
      padding: 4px 0; 
      font-size: 8pt;
    }
    .totals-row.grand-total { 
      font-size: 9pt; 
      font-weight: bold; 
      color: #000; 
      border-top: 2px solid #000; 
      margin-top: 6px; 
      padding-top: 8px; 
    }
    .notes-section { 
      margin-top: 20px; 
      padding: 12px; 
      border: 1px solid #333;
      page-break-inside: avoid;
    }
    .notes-label { 
      font-weight: 600; 
      color: #000; 
      margin-bottom: 6px;
      font-size: 8pt;
    }
    .footer { 
      position: absolute;
      bottom: 30px;
      left: 30px;
      right: 30px;
      padding-top: 12px; 
      border-top: 1px solid #333; 
      text-align: center; 
      font-size: 9pt; 
      color: #000;
      page-break-inside: avoid;
    }
    .info-row {
      margin-bottom: 2px;
    }
    @media print {
      .page { 
        margin: 0; 
        box-shadow: none; 
        page-break-after: always;
      }
      @page { 
        size: A4; 
        margin: 0; 
      }
      tr {
        page-break-inside: avoid !important;
      }
    }
  </style>
</head>
<body>
  <div class="page">
    <!-- Watermark -->
    <div class="watermark">${docTitle}</div>
    
    <!-- Header -->
    <div class="header">
      <!-- Supplier Info (Left - under watermark) -->
      <div class="supplier-info">
        <div class="supplier-name">${supplier.name || 'Supplier Name'}</div>
        <div class="supplier-details">
          ${supplierAddressLine1 ? `${supplierAddressLine1}<br/>` : ''}
          ${supplierAddressLine2 ? `${supplierAddressLine2}<br/>` : ''}
          ${supplier.vatNumber ? `${labels.vat}: ${supplier.vatNumber}` : ''}
        </div>
      </div>
      
      <!-- Invoice Info Top Right -->
      <div class="invoice-info">
        <div class="invoice-number-line">
          <span class="label">${labels.invoiceNumber}:</span>
          <span class="value">${invoiceNo || 'N/A'}</span>
        </div>
        <div class="invoice-date">${labels.issueDate}: ${formatDate(issueDate)}</div>
        ${dueDate ? `<div class="invoice-date">${labels.dueDate}: ${formatDate(dueDate)}</div>` : ''}
        <div class="customer-section">
          <strong style="text-align: justify;display: inline-block;">${labels.billTo}:</strong><br/>
          <strong>${customer.name || 'Customer Name'}</strong><br/>
          ${customerAddressLine1 ? `${customerAddressLine1}<br/>` : ''}
          ${customerAddressLine2 ? `${customerAddressLine2}<br/>` : ''}
          ${customer.vatNumber ? `${labels.vat}: ${customer.vatNumber}` : ''}
        </div>
      </div>
    </div>

    <!-- Line Items Table -->
    <table>
      <thead>
        <tr>
          <th style="width: 5%; text-align: center;">#</th>
          <th style="width: 40%;">${labels.description}</th>
          <th style="width: 10%; text-align: center;">${labels.quantity}</th>
          
          <th style="width: 13%; text-align: right;">Amount excl. VAT</th>
          <th style="width: 10%; text-align: center;">${labels.vat}</th>
          <th style="width: 12%; text-align: right;">${labels.total}</th>
        </tr>
      </thead>
      <tbody>
        ${lineItemsHtml}
      </tbody>
    </table>

    <!-- Payment Details (Left) and Totals (Right) -->
    ${hasPaymentDetails ? `
      <div class="payment-details">
        <div class="payment-details-label">${labels.paymentDetails}</div>
        <div class="payment-details-content">
          ${payment.accountNumber ? `<strong>${labels.accountNumber}:</strong> ${payment.accountNumber}<br/>` : ''}
          ${payment.termsNote ? `<strong>${labels.terms}:</strong> ${payment.termsNote}` : ''}
        </div>
      </div>
    ` : ''}
    
    <div class="totals">
      <div class="totals-row">
        <span>${labels.subtotal}:</span>
        <span>€${Number(amounts.totalExcludingVAT || 0).toFixed(2)}</span>
      </div>
      <div class="totals-row">
        <span>${labels.vatAmount}:</span>
        <span>€${Number(amounts.totalVAT || 0).toFixed(2)}</span>
      </div>
      <div class="totals-row">
        <span>${labels.totalAmount}:</span>
        <span>€${Number((amounts.totalExcludingVAT || 0) + (amounts.totalVAT || 0)).toFixed(2)}</span>
      </div>
      ${amounts.prepaidAmount && amounts.prepaidAmount > 0 ? `
      <div class="totals-row">
        <span>${labels.prepaidAmount}:</span>
        <span>€${Number(amounts.prepaidAmount || 0).toFixed(2)}</span>
      </div>
      ` : ''}
      <div class="totals-row grand-total">
        <span>${labels.payableAmount}:</span>
        <span>€${Number(amounts.payableAmount || 0).toFixed(2)}</span>
      </div>
    </div>
    <div style="clear: both;"></div>

    <!-- Notes -->
    ${notes ? `
      <div class="notes-section">
        <div class="notes-label">${labels.notes}:</div>
        <div>${notes}</div>
      </div>
    ` : ''}

    <!-- Footer -->
    <div class="footer">
      <div style="font-weight: 600;">${isCreditNote ? labels.creditNoteFooter : labels.invoiceFooter}</div>
    </div>
  </div>
</body>
</html>
  `;

  return html;
};

/**
 * Generate and preview PDF using html2canvas and jsPDF
 */
export const generateAndPreviewPeppolInvoicePDF = async (invoiceData, documentType = 'Invoice', language = 'en') => {
  try {
    // Generate HTML
    const filledTemplate = generatePeppolInvoicePdfHtml(invoiceData, documentType, language);

    // Create temporary div
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = filledTemplate;
    
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.width = '794px'; // A4 width in pixels at 96 DPI
    tempDiv.style.background = 'white';
    document.body.appendChild(tempDiv);

    // Wait for rendering
    await new Promise(resolve => setTimeout(resolve, 100));

    // Generate canvas
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      logging: false,
      width: 794,
      windowWidth: 794,
      backgroundColor: '#ffffff',
      onclone: (clonedDoc) => {
        clonedDoc.body.style.background = 'white';
      }
    });

    document.body.removeChild(tempDiv);

    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      throw new Error('Invalid canvas generated');
    }

    // Create PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    // Handle multi-page if needed
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 10) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }
    
    // Add page numbers to all pages
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(85, 85, 85);
      pdf.text(`${i}/${totalPages}`, pdfWidth - 15, pdfHeight - 10, { align: 'right' });
    }

    // Open in new tab
    const pdfBlob = pdf.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
    
    return true;
  } catch (error) {
    console.error('❌ Peppol Invoice PDF generation error:', error);
    throw error;
  }
};

/**
 * Get labels for different languages
 */
function getLabels(language) {
  const translations = {
    en: {
      invoice: 'Invoice',
      creditNote: 'Credit Note',
      issueDate: 'Issue Date',
      dueDate: 'Due Date',
      invoiceNumber: 'Invoice Number',
      billTo: 'Bill To',
      vat: 'VAT',
      identifier: 'Identifier',
      paymentDetails: 'Payment Details',
      accountNumber: 'Account Number',
      terms: 'Terms',
      description: 'Description',
      quantity: 'Quantity',
      unit: 'Unit',
      amountExclVAT: 'Amount excl. VAT',
      total: 'Total',
      subtotal: 'Subtotal (Excl. VAT)',
      vatAmount: 'VAT Amount',
      totalAmount: 'Total Amount',
      prepaidAmount: 'Prepaid Amount',
      payableAmount: 'Payable Amount',
      notes: 'Notes',
      invoiceFooter: 'This is a computer-generated invoice',
      creditNoteFooter: 'This is a computer-generated credit note',
    },
    fr: {
      invoice: 'Facture',
      creditNote: 'Note de Crédit',
      issueDate: 'Date d\'émission',
      dueDate: 'Date d\'échéance',
      invoiceNumber: 'Numéro de facture',
      billTo: 'Facturer à',
      vat: 'TVA',
      identifier: 'Identifiant',
      paymentDetails: 'Détails de paiement',
      accountNumber: 'Numéro de compte',
      terms: 'Conditions',
      description: 'Description',
      quantity: 'Quantité',
      unit: 'Unité',
      unitPrice: 'Prix unitaire',
      total: 'Total',
      subtotal: 'Sous-total (HT)',
      vatAmount: 'Montant TVA',
      totalAmount: 'Montant total',
      prepaidAmount: 'Montant prépayé',
      payableAmount: 'Montant à payer',
      notes: 'Notes',
      invoiceFooter: 'Ceci est une facture générée par ordinateur',
      creditNoteFooter: 'Ceci est une note de crédit générée par ordinateur',
    },
    nl: {
      invoice: 'Factuur',
      creditNote: 'Creditnota',
      issueDate: 'Uitgiftedatum',
      dueDate: 'Vervaldatum',
      invoiceNumber: 'Factuurnummer',
      billTo: 'Factureren aan',
      vat: 'BTW',
      identifier: 'Identificatie',
      paymentDetails: 'Betalingsgegevens',
      accountNumber: 'Rekeningnummer',
      terms: 'Voorwaarden',
      description: 'Beschrijving',
      quantity: 'Aantal',
      unit: 'Eenheid',
      unitPrice: 'Eenheidsprijs',
      total: 'Totaal',
      subtotal: 'Subtotaal (Excl. BTW)',
      vatAmount: 'BTW-bedrag',
      totalAmount: 'Totaalbedrag',
      prepaidAmount: 'Vooruitbetaald bedrag',
      payableAmount: 'Te betalen bedrag',
      notes: 'Opmerkingen',
      invoiceFooter: 'Dit is een computergegenereerde factuur',
      creditNoteFooter: 'Dit is een computergegenereerde creditnota',
    },
  };

  return translations[language] || translations.en;
}
